import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  AlertTriangle,
  BellRing,
  CalendarClock,
  CheckCircle2,
  ExternalLink,
  FileText,
  MessageSquare,
  RefreshCw,
  Plus,
  Send,
  Shield,
  Trash2,
} from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import {
  useGetDriverCoaching,
  useSaveCoachingNote,
  useIssueCoachingNote,
  useVoidCoachingNote,
  useCheckCoachingSignatures,
  useGetCoachingPreview,
  useGetCoachingDocumentLink,
  useRemindCoachingSignature,
} from '../../hooks/backend/drivers'
import { writeErrorMessage } from '../../utils/writeError'
import { readableDate } from '../../utils/coachingDates'
import { pdfBlobUrl } from '../../utils/loans'

// The coaching log on a driver record.
//
// WHY IT EXISTS. Every coaching conversation Fleetly has ever had happened on a
// phone call or in a WhatsApp thread and left nothing. Nobody could say which
// driver was coached on what, by whom, or whether he agreed with a word of it,
// and nobody could say which driver has not been spoken to in two months.
//
// THE LIMITS ON THE BOXES ARE THE DATABASE'S. 1,152 / 480 / 288 characters are
// the checks on driver_coaching_note, and the counters count down against them so
// a manager sees the wall coming instead of hitting it on save. They were first
// set from a fixed-geometry SignNow page; since migration 0362 the page is drawn
// and reflows, and the limits stay to keep a record short enough to be read
// before it is signed. api/test/coachingRecord.test.ts pins the two together.
//
// SIGNATURES ARE CHECKED WHEN THIS OPENS. Nothing calls back when a coaching
// record is signed (tail-webhooks has no coaching route), so opening the log asks
// SignNow about any record still out, on top of the cron check every 15 minutes.
//
// NOTHING HERE DECIDES ANYTHING. Every label, every status wording, days since,
// whether a follow-up is overdue, whether a note may still be edited: all of it
// arrives from get_driver_coaching_log_v1. CLAUDE.md rule 4.

const LIMIT_DISCUSSED = 1152
const LIMIT_COMMITTED = 480
const LIMIT_RESPONSE = 288

type Topic = {
  topic_key: string
  ordinal: number
  label: string
  gate_key: string | null
  guidance: string | null
}

type Note = {
  note_id: string
  coached_on: string
  channel: string
  channel_label: string
  topic_key: string
  topic_label: string
  gate_key: string | null
  topic_guidance: string | null
  subject: string
  discussed: string
  committed_to: string
  driver_response: string | null
  follow_up_on: string | null
  follow_up_overdue: boolean
  coach_name: string | null
  status: string
  status_label: string
  signed_at: string | null
  void_reason: string | null
  days_since: number
  may_edit: boolean
  send_error: string | null
  manager_signed_at: string | null
  declined_by_role: string | null
  driver_comment: string | null
  has_document: boolean
  send_stalled: boolean
  reminder_count: number
  reminder_line: string | null
  may_remind: boolean
  remind_from: string | null
  reminder_error: string | null
}

type Summary = {
  notes_total: number
  notes_30d: number
  notes_90d: number
  awaiting_signature: number
  signed_total: number
  drafts: number
  follow_ups_overdue: number
  last_coached_on: string | null
  days_since_coached: number | null
}

type PanelData = {
  driver_name: string
  canManage: boolean
  notes: Note[]
  topics: Topic[]
  summary: Summary | null
  disclaimer: string | null
}

const CHANNELS: { value: string; label: string }[] = [
  { value: 'in_person', label: 'In person' },
  { value: 'phone', label: 'Telephone call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'video', label: 'Video call' },
]

function todayISO(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

function statusTone(status: string): string {
  if (status === 'signed') return 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
  if (status === 'sent') return 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200'
  if (status === 'issued') return 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200'
  if (status === 'declined') return 'border-destructive/40 bg-destructive/10 text-destructive'
  if (status === 'void') return 'border-border bg-muted text-muted-foreground'
  return 'border-border bg-card text-muted-foreground'
}

/** Counts down rather than up, and turns red only when it is actually a refusal. */
function Counter({ value, limit }: { value: string; limit: number }) {
  const left = limit - value.length
  return (
    <span
      className={cn(
        'text-[11px] tabular-nums',
        left < 0 ? 'font-semibold text-destructive' : left < 80 ? 'text-amber-600' : 'text-muted-foreground',
      )}
    >
      {left < 0
        ? `${-left} characters too long for the record`
        : `${left} characters left`}
    </span>
  )
}

type Draft = {
  coached_on: string
  channel: string
  topic_key: string
  subject: string
  discussed: string
  committed_to: string
  driver_response: string
  follow_up_on: string
}

const EMPTY: Draft = {
  coached_on: todayISO(),
  channel: 'in_person',
  topic_key: '',
  subject: '',
  discussed: '',
  committed_to: '',
  driver_response: '',
  follow_up_on: '',
}

export function CoachingLogPanel({ applicationId }: { applicationId: string }) {
  const board = useGetDriverCoaching<PanelData>()
  const save = useSaveCoachingNote()
  const issue = useIssueCoachingNote()
  const voidNote = useVoidCoachingNote()
  const check = useCheckCoachingSignatures()
  const preview = useGetCoachingPreview<{ pdf_base64: string; pages: number }>()
  const docLink = useGetCoachingDocumentLink<{ url: string }>()
  const remind = useRemindCoachingSignature<{ ok: boolean; role: string }>()

  const trigger = board.trigger
  const [busy, setBusy] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY)

  const load = useCallback(() => {
    void trigger({ application_id: applicationId }, { skipCache: true })
  }, [trigger, applicationId])

  useEffect(() => {
    load()
  }, [load])

  // Once per opening, and only when something is out for signature: ask SignNow,
  // then reload if anything moved. A failure here is quiet, because cron asks
  // again within 15 minutes and the log is still right about what it knows.
  const checkTrigger = check.trigger
  const [checkedOnOpen, setCheckedOnOpen] = useState(false)
  const anyOut = (board.data?.notes ?? []).some((n) => n.status === 'sent')
  useEffect(() => {
    if (checkedOnOpen || !anyOut) return
    setCheckedOnOpen(true)
    void (async () => {
      try {
        const r = (await checkTrigger({ application_id: applicationId }, { skipCache: true })) as
          | { signed?: number; declined?: number }
          | null
        if ((r?.signed ?? 0) + (r?.declined ?? 0) > 0) load()
      } catch {
        /* cron checks again; nothing to tell the manager */
      }
    })()
  }, [anyOut, applicationId, checkTrigger, checkedOnOpen, load])

  const data = board.data
  const notes = data?.notes ?? []
  const topics = data?.topics ?? []
  const summary = data?.summary ?? null
  const canManage = data?.canManage === true

  const topic = useMemo(
    () => topics.find((t) => t.topic_key === draft.topic_key) ?? null,
    [topics, draft.topic_key],
  )

  const tooLong =
    draft.discussed.length > LIMIT_DISCUSSED ||
    draft.committed_to.length > LIMIT_COMMITTED ||
    draft.driver_response.length > LIMIT_RESPONSE

  const incomplete =
    draft.topic_key === '' ||
    draft.subject.trim().length < 3 ||
    draft.discussed.trim().length < 20 ||
    draft.committed_to.trim().length < 3

  const reset = useCallback(() => {
    setDraft(EMPTY)
    setEditingId(null)
    setFormOpen(false)
  }, [])

  const persist = useCallback(
    async (then: 'draft' | 'send'): Promise<void> => {
      setBusy(true)
      try {
        const res = (await save.trigger(
          {
            application_id: applicationId,
            note_id: editingId,
            patch: {
              coached_on: draft.coached_on,
              channel: draft.channel,
              topic_key: draft.topic_key,
              subject: draft.subject.trim(),
              discussed: draft.discussed.trim(),
              committed_to: draft.committed_to.trim(),
              driver_response: draft.driver_response.trim() || null,
              follow_up_on: draft.follow_up_on || null,
            },
          },
          { skipCache: true },
        )) as { note_id?: string } | null

        const noteId = res?.note_id ?? editingId
        if (then === 'send' && noteId) {
          await issue.trigger(
            { application_id: applicationId, note_id: noteId },
            { skipCache: true },
          )
          toast.success('Going to SignNow now. You sign first; the driver is asked after you.')
          window.setTimeout(load, 8000)
        } else {
          toast.success('Saved as a draft. It is not sent and he cannot see it yet.')
        }
        reset()
        load()
      } catch (err) {
        toast.error(writeErrorMessage(err))
      } finally {
        setBusy(false)
      }
    },
    [applicationId, draft, editingId, issue, load, reset, save],
  )

  const startEdit = useCallback((note: Note) => {
    setEditingId(note.note_id)
    setDraft({
      coached_on: note.coached_on.slice(0, 10),
      channel: note.channel,
      topic_key: note.topic_key,
      subject: note.subject,
      discussed: note.discussed,
      committed_to: note.committed_to,
      driver_response: note.driver_response ?? '',
      follow_up_on: note.follow_up_on ? note.follow_up_on.slice(0, 10) : '',
    })
    setFormOpen(true)
  }, [])

  const sendExisting = useCallback(
    async (noteId: string, mode: 'send' | 'resend') => {
      setBusy(true)
      try {
        await issue.trigger(
          { application_id: applicationId, note_id: noteId, mode },
          { skipCache: true },
        )
        toast.success(
          mode === 'resend'
            ? 'Reissuing. The new invite replaces the old one once SignNow accepts it.'
            : 'Going to SignNow now. The manager signs first, then the driver.',
        )
        load()
        window.setTimeout(load, 8000)
      } catch (err) {
        toast.error(writeErrorMessage(err))
      } finally {
        setBusy(false)
      }
    },
    [applicationId, issue, load],
  )

  const openPreview = useCallback(
    async (noteId: string) => {
      setBusy(true)
      try {
        const r = await preview.trigger(
          { application_id: applicationId, note_id: noteId },
          { skipCache: true },
        )
        if (!r?.pdf_base64) throw new Error('The page could not be drawn. Try again.')
        window.open(pdfBlobUrl(r.pdf_base64), '_blank', 'noopener')
      } catch (err) {
        toast.error(writeErrorMessage(err))
      } finally {
        setBusy(false)
      }
    },
    [applicationId, preview],
  )

  const openSigned = useCallback(
    async (noteId: string) => {
      setBusy(true)
      try {
        const r = await docLink.trigger(
          { application_id: applicationId, note_id: noteId },
          { skipCache: true },
        )
        if (!r?.url) throw new Error('SignNow did not return a link. Try again in a minute.')
        window.open(r.url, '_blank', 'noopener')
      } catch (err) {
        toast.error(writeErrorMessage(err))
      } finally {
        setBusy(false)
      }
    },
    [applicationId, docLink],
  )

  // A record out for signature is reminded, never resent (migration 0363). The
  // database decides whether a reminder may go and says why not in words.
  const sendReminder = useCallback(
    async (noteId: string) => {
      setBusy(true)
      try {
        const r = await remind.trigger(
          { application_id: applicationId, note_id: noteId },
          { skipCache: true },
        )
        toast.success(
          r?.role === 'Driver'
            ? 'Reminder sent. SignNow has emailed the driver the same invite again.'
            : 'Reminder sent. SignNow has emailed the manager the same invite again.',
        )
        load()
      } catch (err) {
        toast.error(writeErrorMessage(err))
        load()
      } finally {
        setBusy(false)
      }
    },
    [applicationId, load, remind],
  )

  const checkNow = useCallback(async () => {
    setBusy(true)
    try {
      const r = (await check.trigger({ application_id: applicationId }, { skipCache: true })) as
        | { signed?: number; declined?: number }
        | null
      const moved = (r?.signed ?? 0) + (r?.declined ?? 0)
      toast.success(moved > 0 ? 'Updated from SignNow.' : 'Nothing new on SignNow yet.')
      load()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }, [applicationId, check, load])

  const doVoid = useCallback(
    async (noteId: string) => {
      // A reason of at least ten characters, refused by the database if it is
      // shorter. Asked for here rather than after the click fails.
      const reason = window.prompt(
        'Why is this note being voided? The driver can see this, so write a sentence he would understand.',
        '',
      )
      if (reason == null) return
      setBusy(true)
      try {
        await voidNote.trigger(
          { application_id: applicationId, note_id: noteId, reason },
          { skipCache: true },
        )
        toast.success('Voided. It stays on the log with your reason.')
        load()
      } catch (err) {
        toast.error(writeErrorMessage(err))
      } finally {
        setBusy(false)
      }
    },
    [applicationId, load, voidNote],
  )

  if (board.error) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{board.error}</span>
      </div>
    )
  }

  if (!data && board.loading) {
    return (
      <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Loading the coaching log…
      </div>
    )
  }

  if (!data) return null

  const never = summary == null || summary.last_coached_on == null

  return (
    <div className="space-y-4">
      {/* THE CADENCE LINE. A driver nobody has written down a conversation with is
          the finding, so the never case is amber rather than a quiet zero. */}
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border p-3 text-sm',
          never
            ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200'
            : 'border-border bg-card text-foreground',
        )}
      >
        {never ? (
          <>
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              No coaching has ever been recorded for {data.driver_name}. That is a fact about
              Fleetly, not about him.
            </span>
          </>
        ) : (
          <>
            <MessageSquare className="h-4 w-4 shrink-0 text-[hsl(var(--brand))]" />
            <span>
              Last coached{' '}
              <span className="font-semibold">
                {summary!.days_since_coached === 0
                  ? 'today'
                  : `${summary!.days_since_coached} day(s) ago`}
              </span>
              , on {readableDate(summary!.last_coached_on)}.
            </span>
            <span className="text-muted-foreground">
              {summary!.notes_30d} in the last 30 days, {summary!.notes_90d} in 90.
            </span>
            {summary!.awaiting_signature > 0 && (
              <span className="text-amber-700 dark:text-amber-300">
                {summary!.awaiting_signature} waiting on a signature.
              </span>
            )}
            {summary!.follow_ups_overdue > 0 && (
              <span className="font-medium text-destructive">
                {summary!.follow_ups_overdue} follow-up(s) overdue.
              </span>
            )}
          </>
        )}
      </div>

      {canManage && !formOpen && (
        <Button type="button" variant="default" onClick={() => setFormOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          Record a coaching conversation
        </Button>
      )}

      {canManage && formOpen && (
        <section className="space-y-3 rounded-lg border border-border bg-card p-3 sm:p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="coached_on">When did you speak to him</Label>
              <Input
                id="coached_on"
                type="date"
                max={todayISO()}
                value={draft.coached_on}
                onChange={(e) => setDraft({ ...draft, coached_on: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="channel">How it took place</Label>
              <NativeSelect
                id="channel"
                value={draft.channel}
                onChange={(e) => setDraft({ ...draft, channel: e.target.value })}
              >
                {CHANNELS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>

          <div>
            <Label htmlFor="topic">What it was about</Label>
            <NativeSelect
              id="topic"
              value={draft.topic_key}
              onChange={(e) => setDraft({ ...draft, topic_key: e.target.value })}
            >
              <option value="">Choose a topic…</option>
              {topics.map((t) => (
                <option key={t.topic_key} value={t.topic_key}>
                  {t.label}
                  {t.gate_key ? ' (a Tier 2 gate)' : ''}
                </option>
              ))}
            </NativeSelect>
            {/* The topic's own guidance, out of the database, so the rule the
                manager needs is on the screen at the moment he needs it. The fuel
                one is the important one: a car that drifts across drivers goes to
                the workshop, not to a performance conversation. */}
            {topic?.guidance && (
              <p className="mt-1.5 flex items-start gap-1.5 text-xs text-muted-foreground">
                <Shield className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--brand))]" />
                <span>{topic.guidance}</span>
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={draft.subject}
              maxLength={96}
              placeholder="Fuel at 6.1 L/100km on ML91SKGP"
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
            />
          </div>

          <div>
            <div className="flex items-baseline justify-between gap-2">
              <Label htmlFor="discussed">What was discussed</Label>
              <Counter value={draft.discussed} limit={LIMIT_DISCUSSED} />
            </div>
            <Textarea
              id="discussed"
              rows={6}
              value={draft.discussed}
              placeholder="What you put to him, what he said, and what you agreed the facts were."
              onChange={(e) => setDraft({ ...draft, discussed: e.target.value })}
            />
          </div>

          <div>
            <div className="flex items-baseline justify-between gap-2">
              <Label htmlFor="committed_to">What he committed to</Label>
              <Counter value={draft.committed_to} limit={LIMIT_COMMITTED} />
            </div>
            <Textarea
              id="committed_to"
              rows={3}
              value={draft.committed_to}
              placeholder="One thing he can actually do. If nothing was agreed, say that."
              onChange={(e) => setDraft({ ...draft, committed_to: e.target.value })}
            />
          </div>

          <div>
            <div className="flex items-baseline justify-between gap-2">
              <Label htmlFor="driver_response">What he said in reply (optional)</Label>
              <Counter value={draft.driver_response} limit={LIMIT_RESPONSE} />
            </div>
            <Textarea
              id="driver_response"
              rows={2}
              value={draft.driver_response}
              placeholder="In his words, especially if he disagreed."
              onChange={(e) => setDraft({ ...draft, driver_response: e.target.value })}
            />
          </div>

          <div className="sm:w-1/2">
            <Label htmlFor="follow_up_on">Follow up on (optional)</Label>
            <Input
              id="follow_up_on"
              type="date"
              value={draft.follow_up_on}
              onChange={(e) => setDraft({ ...draft, follow_up_on: e.target.value })}
            />
          </div>

          {/* WHAT HE WILL BE ASKED TO SIGN, shown before it is sent, read from the
              same database function that prints it on the page so the two cannot
              drift. */}
          {data.disclaimer && (
            <div className="rounded-md border border-border bg-muted/50 p-3">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Printed on the document he signs
              </p>
              <p className="text-xs leading-relaxed text-foreground">{data.disclaimer}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={busy || tooLong || incomplete}
              onClick={() => void persist('draft')}
            >
              {busy ? <Spinner className="mr-1.5 h-4 w-4" /> : null}
              Save as a draft
            </Button>
            <Button
              type="button"
              disabled={busy || tooLong || incomplete}
              onClick={() => void persist('send')}
            >
              <Send className="mr-1.5 h-4 w-4" />
              Save and send for signature
            </Button>
            <Button type="button" variant="ghost" disabled={busy} onClick={reset}>
              Cancel
            </Button>
          </div>
          {incomplete && (
            <p className="text-xs text-muted-foreground">
              A topic, a subject, an account of the conversation and what he committed to. A note
              that says "spoke to him" records that he was contacted, not that he was coached.
            </p>
          )}
        </section>
      )}

      {/* THE LOG */}
      {notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nothing recorded yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li
              key={n.note_id}
              className={cn(
                'rounded-lg border border-border bg-card p-3',
                n.status === 'void' && 'opacity-70',
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-foreground">
                  {readableDate(n.coached_on)}
                </span>
                <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                  {n.channel_label}
                </span>
                <span className="rounded-full border border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] px-2 py-0.5 text-[11px] text-[hsl(var(--brand-soft-foreground))]">
                  {n.topic_label}
                </span>
                <span
                  className={cn(
                    'rounded-full border px-2 py-0.5 text-[11px]',
                    statusTone(n.status),
                  )}
                >
                  {n.status === 'signed' ? (
                    <CheckCircle2 className="mr-1 inline h-3 w-3" />
                  ) : null}
                  {n.status_label}
                </span>
                {n.follow_up_overdue && (
                  <span className="rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-[11px] text-destructive">
                    <CalendarClock className="mr-1 inline h-3 w-3" />
                    Follow-up overdue
                  </span>
                )}
              </div>

              <p className="mt-1.5 text-sm font-medium text-foreground">{n.subject}</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{n.discussed}</p>
              <p className="mt-2 text-sm text-foreground">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  He committed to{' '}
                </span>
                {n.committed_to}
              </p>
              {n.driver_response && (
                <p className="mt-1.5 border-l-2 border-border pl-2 text-sm italic text-muted-foreground">
                  {n.driver_response}
                </p>
              )}
              {n.send_error && n.status === 'issued' && (
                <p className="mt-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                  {n.send_error}
                </p>
              )}
              {n.reminder_line && (
                <p className="mt-2 text-xs text-muted-foreground">{n.reminder_line}</p>
              )}
              {n.reminder_error && n.status === 'sent' && (
                <p className="mt-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                  {n.reminder_error}
                </p>
              )}
              {n.driver_comment && (
                <p className="mt-2 text-sm text-foreground">
                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    The driver wrote on the signed page{' '}
                  </span>
                  {n.driver_comment}
                </p>
              )}
              {n.void_reason && (
                <p className="mt-2 text-sm text-muted-foreground">
                  <span className="font-semibold">Voided: </span>
                  {n.void_reason}
                </p>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Coached by {n.coach_name ?? 'someone no longer on record'}
                {n.follow_up_on ? `, follow up on ${readableDate(n.follow_up_on)}` : ''}
              </p>

              {n.status !== 'void' && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => void openPreview(n.note_id)}
                  >
                    <FileText className="mr-1.5 h-3.5 w-3.5" />
                    Preview the page
                  </Button>
                  {n.has_document && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => void openSigned(n.note_id)}
                    >
                      <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                      {n.status === 'signed' ? 'Open the signed page' : 'Open on SignNow'}
                    </Button>
                  )}
                  {n.status === 'sent' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => void checkNow()}
                    >
                      <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                      Check signatures
                    </Button>
                  )}
                  {canManage && n.may_edit && (
                    <Button type="button" variant="outline" size="sm" onClick={() => startEdit(n)}>
                      Edit
                    </Button>
                  )}
                  {canManage && (n.status === 'draft' || n.send_stalled) && (
                    <Button
                      type="button"
                      size="sm"
                      disabled={busy}
                      onClick={() => void sendExisting(n.note_id, 'send')}
                    >
                      <Send className="mr-1.5 h-3.5 w-3.5" />
                      {n.status === 'issued' ? 'Send again' : 'Send for signature'}
                    </Button>
                  )}
                  {canManage && n.status === 'sent' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy || !n.may_remind}
                      title={
                        !n.may_remind && n.remind_from
                          ? `A reminder can go once an hour. Next from ${new Date(n.remind_from).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Johannesburg' })}.`
                          : undefined
                      }
                      onClick={() => void sendReminder(n.note_id)}
                    >
                      <BellRing className="mr-1.5 h-3.5 w-3.5" />
                      Send a reminder
                    </Button>
                  )}
                  {canManage && n.status === 'declined' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => void sendExisting(n.note_id, 'resend')}
                    >
                      Reissue
                    </Button>
                  )}
                  {canManage && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => void doVoid(n.note_id)}
                    >
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                      Void
                    </Button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
